import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { LoadDetail, FilteredSaleOrder, LoadManagementService } from '../../services/load-management.service';
import { CartComponent } from '../cart-component/cart-component';
import { SelectedProduct } from '../../services/products';
import { CustomerService, Customer } from '../../services/customer';
import { SalesOrderService } from '../../services/sales-order';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { MessageDialogService } from '../../services/message-dialog';
import { SalesOrdersTableComponent } from '../sales-orders-table-component/sales-orders-table-component';
import { MatButtonModule } from '@angular/material/button';
import { NotificationService } from '../../services/notification';
import { AuthService } from '../../services/auth-service';

@Component({
  selector: 'app-load-viewer',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    SalesOrdersTableComponent,
    CartComponent
  ],
  templateUrl: './load-viewer-component.html',
  styleUrls: ['./load-viewer-component.scss']
})
export class LoadViewerComponent implements OnInit {
  @Input() load: LoadDetail | null = null;
  @Output() closedLoadDetails = new EventEmitter();
  @Output() reloadDetails = new EventEmitter();

  showCart: boolean = false;
  selectedCustomerFromList: Customer | null = null;
  selectedProducts: SelectedProduct[] = [];
  enrichedOrders: any[] = [];
  isSuperVisor: boolean = false;
  destroy$ = new Subject<void>();

  constructor(
    private customerService: CustomerService,
    private salesOrderService: SalesOrderService,
    private loadManagementService: LoadManagementService,
    private messageDialogService: MessageDialogService,
    private notificationService: NotificationService,
    private authService: AuthService
  ) { }


  ngOnInit(): void {
    this.authService.getCurrentUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe(userInfo => this.isSuperVisor = userInfo ? userInfo.isDeliverySupervisor : false);

    this.enrichedOrders = this.load?.saleOrders.map((order) => ({
      ...order,
      selected: true
    })) ?? [];
  }

  get netAmount(): number {
    return (
      this.load?.saleOrders.reduce(
        (sum, order) => sum + (order.totalAmount || 0),
        0
      ) ?? 0
    );
  }

  async viewOrderDetails(order: FilteredSaleOrder): Promise<void> {
    try {
      const response = await firstValueFrom(this.salesOrderService.getSalesOrderById(order.id));
      if (response.status !== 'success') {
        this.notificationService.showMessage(response.message || 'Failed to fetch order');
        return;
      }

      const orderDetails = response.order;
      if (!orderDetails) return;

      this.selectedProducts = orderDetails.lines.map(line => ({
        productId: line.product_id,
        quantity: line.quantity,
        price: line.price_unit
      }));

      const customers = await firstValueFrom(this.customerService.get());
      this.selectedCustomerFromList = customers.find(c => c.id === orderDetails.customerId) ?? null;
      this.showCart = true;
    } catch (err) {
      this.notificationService.showMessage('Failed to load sales order');
    }
  }

  closeCartView(): void {
    this.showCart = false;
  }

  onCloseDetails() {
    if (this.showCart)
      this.showCart = false;
    else
      this.closedLoadDetails.emit();
  }

  updateLoad(): void {
    if (!this.load) {
      this.messageDialogService.warning('No load is selected');
      return;
    }

    if (!this.isSuperVisor && this.load.deliveryBoyId) {
      this.messageDialogService.warning('This load is already assigned');
      return;
    }

    if (!this.enrichedOrders.some(o => o.selected)) {
      this.messageDialogService.warning('No orders selected update.');
      return;
    }

    const payload = {
      load_id: this.load.id,
      write_date: this.load.write_date,
      sale_order_ids: this.enrichedOrders.filter(o => o.selected).map((order, i) => ({
        id: order.id,
        sequence: i + 1
      })),
      note: this.load.note
    };

    this.loadManagementService.updateLoad(payload).subscribe((res) => {
      if (res.status === 'success') {
        this.messageDialogService.success('Load updated successfully.');
      } else {
        this.messageDialogService.error(res.message || 'Failed to update load.');
      }
    }, err => this.messageDialogService.error(err.error.message || 'Failed to update load.'));
  }

  deleteLoad(): void {
    if (!this.load) {
      this.messageDialogService.warning('No load is selected');
      return;
    }

    this.messageDialogService.confirm('Are you sure you want to delete this load?')
      .subscribe((confirmed) => {
        if (!confirmed) return;

        const payload = {
          load_id: this.load!.id,
          write_date: this.load!.write_date
        };

        this.loadManagementService.deleteLoad(payload).subscribe((res) => {
          if (res.status === 'success') {
            this.messageDialogService.success('Load deleted successfully.');
            this.reloadDetails.emit(); // Close or refresh the view
          } else {
            this.messageDialogService.error(res.message || 'Failed to delete load.');
          }
        });
      });
  }

  showAllOrders() {
    this.loadManagementService.filterSaleOrders({ has_load: false }).subscribe(result => {
      this.enrichedOrders = [
        ...this.enrichedOrders, ...result.orders!.filter(order => !this.enrichedOrders.some(so => so.id === order.id))
          .map(order => ({ ...order, selected: false }))
      ];
    })
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
